import { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Camera, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Separator } from '@/shared/components/ui/separator';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FloatingLabelAsyncCombobox } from '@/shared/components/custom/async-combobox/FloatingLabelAsyncCombobox';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/ColorPickerPopover';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { ThemeSection } from './ThemeSection';
import { LanguageSection } from './LanguageSection';
import { DateFormatSection } from './DateFormatSection';
import { TimeFormatSection } from './TimeFormatSection';
import { ProfileSkeleton } from './profile/ProfileSkeleton';
import { useProfile } from '../../hooks/useProfile';
import { useUpdateProfile } from '../../hooks/useUpdateProfile';
import { useUpdatePreferences } from '../../hooks/useUpdatePreferences';
import { useUploadAvatar } from '../../hooks/useUploadAvatar';
import { useDeleteAvatar } from '../../hooks/useDeleteAvatar';
import { createUpdateProfileSchema, type UpdateProfileInput } from '../../schemas';
import { COMMON_TIMEZONES } from '../../config/timezones';
import { useUIStore } from '@/core/stores/ui.store';
import { getColorValue } from '@/core/lib/colors';
import { SUPPORTED_LANGUAGES } from '../../schemas';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import { FileUploader } from '@/shared/components/custom/file-uploader/FileUploader';
import { ImageCropDialog } from '@/shared/components/custom/image-crop/ImageCropDialog';
import { SettingsSectionHeader } from '../SettingsSectionHeader';

type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const DEFAULT_COLOR = 'indigo-violet';

const TIMEZONE_OPTIONS = COMMON_TIMEZONES.map((tz) => ({
  id: tz.value,
  label: tz.label,
}));

export function ProfileSection() {
  const { t } = useTranslation('settings');

  const { data: profile, isLoading, isError } = useProfile();
  const updateProfile = useUpdateProfile();
  const updatePreferences = useUpdatePreferences();
  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const { toast } = useToast();

  const timezone = useUIStore((s) => s.timezone);
  const setTimezone = useUIStore((s) => s.setTimezone);
  const serverLanguage = profile?.preferences?.language;

  const isInitialized = useRef(false);

  const form = useForm<UpdateProfileInput>({
    resolver: zodResolver(createUpdateProfileSchema(t)),
    defaultValues: { displayName: '', color: DEFAULT_COLOR, avatarUrl: undefined },
  });

  const {
    formState: { isDirty, isSubmitting },
  } = form;
  const watchedDisplayName = form.watch('displayName');
  const watchedColor = form.watch('color') ?? DEFAULT_COLOR;
  const watchedAvatarUrl = form.watch('avatarUrl');

  useEffect(() => {
    if (!profile) return;
    form.reset({
      displayName: profile.displayName ?? '',
      color: profile.color ?? DEFAULT_COLOR,
      avatarUrl: profile.avatarUrl ?? undefined,
    });
  }, [profile, form]);

  useEffect(() => {
    if (!profile?.preferences || isInitialized.current) return;
    isInitialized.current = true;

    const prefs = profile.preferences;
    if (prefs.timezone) {
      setTimezone(prefs.timezone);
    }
  }, [profile?.preferences, setTimezone]);

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await updateProfile.mutateAsync(data);
      form.reset(data);
    } catch {
      // global handler shows error toast
    }
  });

  const handleLanguageChange = (lang: SupportedLanguage) => {
    updatePreferences.mutate({ language: lang });
  };

  const handleTimezoneChange = (value: string | null) => {
    if (value !== null) {
      setTimezone(value);
      updatePreferences.mutate({ timezone: value });
    }
  };

  const openUploaderRef = useRef<(() => void) | null>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  const handleAvatarFilesAccepted = (_files: File[], previewUrls: string[]) => {
    if (previewUrls[0]) setCropImageSrc(previewUrls[0]);
  };

  const handleAvatarFilesRejected = () => {
    toast.error({ title: t('profile.avatar.typeError') });
  };

  const handleCropConfirm = (blob: Blob) => {
    setCropImageSrc(null);
    uploadAvatar.mutate(new File([blob], 'avatar.jpg', { type: blob.type }));
  };

  const handleCropCancel = () => {
    setCropImageSrc(null);
  };

  const handleAvatarRemove = async () => {
    const result = await confirm({
      title: t('profile.avatar.remove'),
      description: t('profile.avatar.remove'),
      variant: 'destructive',
    });
    if (result.confirmed) {
      deleteAvatar.mutate();
    }
  };

  if (isLoading) return <ProfileSkeleton />;

  if (isError) {
    return (
      <div data-testid="settings-profile-error" className="text-sm text-destructive py-4">
        {t('profile.loadError')}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-profile-section">
      {/* ── Datos personales ────────────────────────────────── */}
      <div>
        <div className="pb-6">
          <SettingsSectionHeader>{t('profile.userDataTitle')}</SettingsSectionHeader>
          <p className="text-sm text-muted-foreground mt-1">{t('profile.userDataDescription')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-10">
          <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-8 items-start">
            <div className="flex justify-center">
              <FileUploader
                accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] }}
                maxSize={2 * 1024 * 1024}
                onFilesAccepted={handleAvatarFilesAccepted}
                onFilesRejected={handleAvatarFilesRejected}
              >
                {({ getInputProps, open }) => {
                  openUploaderRef.current = open;
                  return (
                    <div className="relative">
                      <UserAvatar
                        name={watchedDisplayName || '?'}
                        color={watchedColor || DEFAULT_COLOR}
                        src={watchedAvatarUrl ?? profile?.avatarUrl ?? undefined}
                        size="2xl"
                        className="ring-2 ring-background"
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="absolute bottom-1 right-1 flex size-7 items-center justify-center rounded-full bg-foreground text-background shadow-md hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            data-testid="settings-profile-avatar-edit"
                          >
                            <Camera className="size-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center">
                          <DropdownMenuItem onClick={() => openUploaderRef.current?.()}>
                            {t('profile.avatar.upload')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={handleAvatarRemove}
                            disabled={!profile?.avatarUrl}
                          >
                            {t('profile.avatar.remove')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <input {...getInputProps()} data-testid="settings-profile-avatar-input" />
                    </div>
                  );
                }}
              </FileUploader>
              <ImageCropDialog
                open={!!cropImageSrc}
                imageSrc={cropImageSrc}
                cropShape="round"
                onConfirm={handleCropConfirm}
                onCancel={handleCropCancel}
                labels={{
                  title: t('profile.avatar.crop.title'),
                  zoom: t('profile.avatar.crop.zoom'),
                  rotate: t('profile.avatar.crop.rotate'),
                  confirm: t('profile.avatar.crop.confirm'),
                  cancel: t('profile.avatar.crop.cancel'),
                }}
              />
            </div>

            <div className="space-y-6">
              <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">{t('profile.colorLabel')}</p>
                <div className="flex items-center gap-3">
                  <Controller
                    control={form.control}
                    name="color"
                    render={({ field }) => (
                      <ColorPickerPopover
                        value={field.value}
                        onSelect={(token) => field.onChange(token)}
                        trigger={
                          <button
                            type="button"
                            data-testid="settings-profile-color-trigger"
                            aria-label={t('profile.colorLabel')}
                            className="size-8 rounded-full shrink-0 ring-2 ring-offset-2 ring-offset-background ring-border hover:ring-primary transition-all focus-visible:outline-none focus-visible:ring-primary"
                            style={{ background: getColorValue(field.value) || field.value }}
                          />
                        }
                      />
                    )}
                  />
                  <p className="text-xs text-muted-foreground">{t('profile.colorDescription')}</p>
                </div>
              </div>

              <FormFieldInput
                control={form.control}
                name="displayName"
                label={t('profile.displayName')}
                floatingLabel
                data-testid="settings-profile-displayname-input"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!isDirty || isSubmitting}
              data-testid="settings-profile-save-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('profile.saving')}
                </>
              ) : (
                t('profile.save')
              )}
            </Button>
          </div>
        </form>
      </div>

      <Separator />

      {/* ── Apariencia ─────────────────────────────────────── */}
      <div>
        <div className="pb-6">
          <SettingsSectionHeader>{t('theme.title')}</SettingsSectionHeader>
          <p className="text-sm text-muted-foreground mt-1">{t('theme.description')}</p>
        </div>
        <ThemeSection showHeader={false} />
      </div>

      <Separator />

      {/* ── Idioma ─────────────────────────────────────────── */}
      <div>
        <div className="pb-6">
          <SettingsSectionHeader>{t('language.title')}</SettingsSectionHeader>
          <p className="text-sm text-muted-foreground mt-1">{t('language.description')}</p>
        </div>
        <LanguageSection
          showHeader={false}
          serverLanguage={serverLanguage}
          onServerLanguageChange={handleLanguageChange}
        />
      </div>

      <Separator />

      {/* ── Regional ───────────────────────────────────────── */}
      <div data-testid="settings-regional-section">
        <div className="pb-6">
          <SettingsSectionHeader>{t('regional.title')}</SettingsSectionHeader>
          <p className="text-sm text-muted-foreground mt-1">{t('regional.description')}</p>
        </div>

        <div className="space-y-4">
          <FloatingLabelAsyncCombobox
            id="settings-timezone"
            label={t('profile.timezone.label')}
            queryKey={['settings', 'timezones']}
            staticOptions={TIMEZONE_OPTIONS}
            value={timezone || null}
            onChange={handleTimezoneChange}
            placeholder={t('profile.timezone.placeholder')}
          />

          <DateFormatSection showHeader={false} />

          <TimeFormatSection showHeader={false} />

          {updatePreferences.isPending && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              {t('preferences.saving')}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        title={options?.title ?? ''}
        description={options?.description}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
